import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreatedEvent } from "../../redux/features/event/event.types";
import { useAppSelector } from "../../redux/hooks";
import {
  copyTextToClipboard,
  formatDate,
  getEventUrl,
} from "../../utils/helpers";
import { Button } from "../buttons/button";
import { Icon } from "../icon/icon";
import { EventCard } from "./eventCard";

export const FullEventCard = ({
  event,
  idx,
}: {
  event: CreatedEvent;
  idx?: number;
}) => {
  const navigate = useNavigate();

  const [buttonText, setButtonText] = useState("Copy Event Link");

  const { isSignedIn } = useAppSelector((state) => state.auth);

  const handleCopy = async () => {
    await copyTextToClipboard(getEventUrl(event.slug));
    setButtonText("Copied!");
    setTimeout(() => {
      setButtonText("Copy Event Link");
    }, 2000);
  };

  const handleCardClick = () => {
    if (event.status === "draft" && event.userType === "organiser") {
      navigate(`/event/${event.slug}/edit`);
    } else if (event.userType === "organiser") {
      navigate(`/events/${event.slug}/details`);
    } else if (event.userType === "guest" && isSignedIn) {
      navigate(`/events/guest/${event.guestId}/ticket`);
    } else if (event.userType === "guest" && !isSignedIn) {
      navigate(`/events/${idx}/ticket/guest`);
    } else {
      navigate(`/events/${event.slug}/preview`);
    }
  };

  return (
    <div className="flex gap-[2px]">
      <EventCard
        image={event.image}
        name={event.name}
        location={event.location}
        startDate={formatDate(event.startDate)}
        endDate={formatDate(event.endDate)}
        startTime={event.startTime}
        endTime={event.endTime}
        event={event}
        noTickets={event.ticketCount ?? 0}
        totalGuests={event.totalGuests}
        isOrganizer={event.userType === "organiser"}
        className=""
        clickable
        onClick={() => {
          handleCardClick();
        }}
      >
        <div className="grid w-full grid-cols-1 gap-2.5 lg:flex-1 lg:grid-cols-2 lg:items-end">
          {/* if is organizer and event is draft  */}
          {event.status === "draft" && event.userType === "organiser" && (
            <Button
              type="button"
              icon="pencil"
              text="Edit Event"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/event/${event.slug}/edit`);
              }}
              variation="plain-grey"
            />
          )}

          {/* If is organiser and event not draft  */}
          {event.status !== "draft" && event.userType === "organiser" && (
            <Button
              type="button"
              icon="share"
              text="Go To Event"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/events/${event.slug}/details`);
              }}
              variation="plain-grey"
            />
          )}

          {/* if is guest and event not draft  */}
          {event.status !== "draft" && event.userType === "guest" && (
            <Button
              type="button"
              icon="download"
              text="Download Ticket"
              onClick={(e) => {
                e.stopPropagation();
                if (isSignedIn) {
                  navigate(`/events/guest/${event.guestId}/ticket`);
                } else {
                  navigate(`/events/${idx}/ticket/guest`);
                }
              }}
              variation="plain-grey"
            />
          )}

          {/* if is organizer and event is draft  */}
          {event.status === "draft" && event.userType === "organiser" && (
            <Button
              type="button"
              icon="arrow-right"
              iconClassName="-rotate-[45deg]"
              text="Publish Event"
              onClick={() => {}}
              variation="plain-grey"
            />
          )}

          {/* Any other scenario and event is not draft  */}
          {event.status !== "draft" && (
            <Button
              type="button"
              icon="link"
              text={buttonText}
              onClick={(e) => {
                e.stopPropagation();
                handleCopy();
              }}
              variation="plain-grey"
            />
          )}
        </div>
      </EventCard>

      <button
        type="button"
        className="soft-shrink hidden !min-h-full w-[82px] max-w-[82px] items-center justify-center rounded-2xl bg-white p-3 md:flex"
        onClick={handleCardClick}
      >
        <Icon svg="caret-down" width={24} height={24} className="-rotate-90" />
      </button>
    </div>
  );
};
